import { useFormik, useFormikContext } from 'formik';
import styles from './GenerateForm.module.scss';
import Button from 'component/atom/forms/button/Button';
import { usePost } from 'hooks/usePost';
import { chat } from 'hooks/endpoints';
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';
import { renderDynamicField } from './factory/renderDynamicField';
import { generateValidationSchema } from './factory/generateValidationSchema';
import { generateInitialValues } from './factory/generateInitialValues';
import fileToBase64 from 'helpers/fileToBase64';

const GenerateForm = ({
  step,
  isWorkFlow,
  chatId,
  botId,
  onChatUpdate,
  setCapture,
  setSuggestedQuestions,
  isExpanded,
  restartForm,
  guestChat,
  isConversation,
  classname,
  triggerNewConversation,
  isNode,
}) => {
  const formikContext = useFormikContext();
  const { mutate: postResponse } = usePost();
  const [isSubmitted, setIsSubmitted] = useState(false);
  useEffect(() => {
    setIsSubmitted(false);
  }, [restartForm]);

  const callEmptyGuest = () => {
    const formData = new FormData();
    formData.append('ChatId', chatId);
    formData.append('BotId', botId);
    postResponse(
      { data: formData, url: chat.postResponse, isFormData: true },
      {
        onSuccess: (res) => {
          setCapture(res?.data.capture);
          onChatUpdate(res.data);
          setSuggestedQuestions(res.data.suggestedQuestions || []);
        },
      }
    );
  };

  const { mutate: submitForm, isPending } = usePost();

  const initialValues = generateInitialValues(step.value.formDetails);
  const validationSchema = isConversation
    ? null
    : generateValidationSchema(step.value.formDetails);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (isConversation) return;

      const formDetails = await Promise.all(
        Object.entries(values).map(async ([key, value]) => {
          const field = step.value.formDetails.find(
            (f) => f.fieldLabel === key
          );

          let fieldValue = value;
          if (value instanceof File) {
            const base64 = await fileToBase64(value);
            fieldValue = `data:${base64.contentType};base64,${base64.fileContent}`;
          } else if (Array.isArray(value)) {
            fieldValue = value.join(',');
          } else if (typeof value === 'number') {
            fieldValue = value.toString();
          }

          return {
            id: field ? field.id : null,
            fieldValue,
          };
        })
      );

      const payload = {
        chatDetailId: guestChat.botChatDetailId,
        formId: step.value.id || 0,
        chatId: chatId || '',
        formDetails,
      };

      submitForm(
        { url: chat.postForm, data: payload },
        {
          onSuccess: () => {
            delete formikContext.values.Question;
            const updatedValues = { ...formikContext.values };
            delete updatedValues.Question;
            setIsSubmitted(true);
            formikContext.setValues(updatedValues, false);
            toast.success('Form submitted successfully');
            formikContext.handleSubmit();
            callEmptyGuest();
          },
          onError: () => toast.error('Something went wrong, Please try again'),
        }
      );
    },
  });

  useEffect(() => {
    formik.resetForm();
  }, [triggerNewConversation]);

  return (
    <form
      onSubmit={formik.handleSubmit}
      className={`${styles['form-response']} ${
        isWorkFlow ? styles['full-width'] : ''
      } ${isNode ? styles['node'] : ''} ${classname ?? ''}`}
    >
      <p className={styles.title}>{step?.value.formTitle}</p>
      {step?.value?.formDetails?.map((field) => (
        <div key={field.id} className={styles['question']}>
          {renderDynamicField(
            field,
            formik,
            isSubmitted,
            isConversation,
            triggerNewConversation
          )}
        </div>
      ))}
      <Button
        label={step?.buttonText ?? step?.value.buttonText}
        className={styles.button}
        onClick={formik.handleSubmit}
        isLoading={isPending}
        disabled={isSubmitted || isConversation}
      />
    </form>
  );
};

export default GenerateForm;
